import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';
import { retryHandlers } from './helpers';
import {
  getUserAlertAggregationSettings,
  updateUserAlertAggregationSettings as updateUserApi,
  getOrgAlertAggregationSettings,
  updateOrgAlertAggregationSettings as updateOrgApi,
} from '@kirin/api-client/api/alert-aggregation';
import type {
  AlertAggregationSettingsResponse,
  UpdateAlertAggregationSettingsBody,
} from '@kirin/api-client/contracts/alert-aggregation';

/**
 * Fetch the current user's personal in-app alert aggregation settings.
 * Returns the Balanced default when no setting exists.
 */
export function useUserAlertAggregationSettings() {
  return useQuery({
    queryKey: queryKeys.alertAggregationSettings.user(),
    queryFn: () => getUserAlertAggregationSettings(apiClient),
    retry: retryHandlers.detail,
  });
}

/**
 * Update the current user's personal alert aggregation settings.
 */
export function useUpdateUserAlertAggregationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateAlertAggregationSettingsBody) =>
      updateUserApi(apiClient, data),
    onSuccess: (data: AlertAggregationSettingsResponse) => {
      queryClient.setQueryData(
        queryKeys.alertAggregationSettings.user(),
        (old: AlertAggregationSettingsResponse | undefined) => ({
          ...old,
          ...data,
        })
      );
    },
  });
}

/**
 * Fetch an organization's in-app alert aggregation settings.
 * Authorization: Owner, Manager, or Member (all can view).
 * Returns the Balanced default when no setting exists.
 */
export function useOrgAlertAggregationSettings(organizationId: string) {
  return useQuery({
    queryKey: queryKeys.alertAggregationSettings.organization(organizationId),
    queryFn: () => getOrgAlertAggregationSettings(apiClient, organizationId),
    enabled: !!organizationId,
    retry: retryHandlers.detail,
  });
}

/**
 * Update an organization's alert aggregation settings.
 * Authorization: Owner or Manager only.
 *
 * Instantiated unconditionally (rules of hooks); mutate() is only called in org
 * context. Guards against an empty organizationId.
 */
export function useUpdateOrgAlertAggregationSettings(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateAlertAggregationSettingsBody) => {
      if (!organizationId) {
        return Promise.reject(
          new Error(
            'Cannot update organization settings: organizationId is required'
          )
        );
      }
      return updateOrgApi(apiClient, organizationId, data);
    },
    onSuccess: (data: AlertAggregationSettingsResponse) => {
      queryClient.setQueryData(
        queryKeys.alertAggregationSettings.organization(organizationId),
        (old: AlertAggregationSettingsResponse | undefined) => ({
          ...old,
          ...data,
        })
      );
    },
  });
}
